import type { Entity, QueriesObject } from '../../../core';
import type { System } from '../../../core/System';
import { Ticker } from '../../../ticker';
import type { XY } from '../../../utils';
import { Container } from '../../display';
import type { Entity2D } from '../Entity2D';
import type { Scene2D } from '../Scene2D';
import { View2DComponent } from './View2DComponent';

export interface View2DSystemOptions {
  stage?: Container;
}

export interface ICamera extends XY {
  zoom: number;
  offset: XY;
}

export class View2DSystem implements System<View2DSystemOptions> {
  public static readonly NAME = 'view2d';
  public static Queries: QueriesObject = {
    view2d: {
      components: [View2DComponent],
      added: true,
      removed: true,
    },
  };

  public w = -1;
  public h = -1;
  public scene!: Scene2D;
  public camera: ICamera = { x: 0, y: 0, zoom: 1, offset: { x: 0, y: 0 } };
  public stage!: Container;

  private readonly _activeItemsContainer: Container = new Container();
  private readonly _layers: Record<string, Container> = {};
  private readonly _innerContainer: Container = new Container();

  public init(opts: View2DSystemOptions = {}) {
    this.stage = opts.stage || new Container();

    this._innerContainer.tag = 'View2DContainer';
    this.stage.addChild(this._innerContainer);

    this._activeItemsContainer.tag = 'View2DActiveItems';
    this._innerContainer.addChild(this._activeItemsContainer);

    this.addLayer('back');
    this.addLayer('default');
    this.addLayer('middle');
    this.addLayer('front');
    this.addLayer('foreground');

    this.resize(1, 1);

    // override the render function...
    // const render = this.stage.render.bind(this.stage);

    Ticker.system.add(() => {
      this.scene.renderStart();

      //   render(renderer);

      this.scene.renderFinish();
    });

    // this.stage.render = () => {
    //   this.scene.renderStart();

    //   //   render(renderer);

    //   this.scene.renderFinish();
    // };
  }

  public setCamera(camera: ICamera): void {
    this.camera = camera;
    this.resize(this.w, this.h);
  }

  public render(): void {
    // const zoom = this.camera.zoom;
    // const stage = this.stage;
    // stage.scaleX = zoom;
    // stage.scaleY = zoom;
    // stage.x = this.w / 2 - (this.w / 2) * zoom + this.camera.offset.x * 2;
    // stage.y = this.h / 2 - (this.h / 2) * zoom + this.camera.offset.y;
    // const sx = this._innerContainer.scaleX;
    // const sy = this._innerContainer.scaleY;
    // this._innerContainer.x = -this.camera.x * sx + this.w / 2;
    // this._innerContainer.y = -this.camera.y * sy + this.h / 2;
  }

  public addedToQuery(ent: Entity): void {
    const entity = ent as Entity2D;
    const view = entity.c.view2d.view;
    const layer = this._layers[entity.c.view2d.layer];

    // view.parent = layer;
    // layer.children.push(view);
    layer.addChild(view);
  }

  public removedFromQuery(ent: Entity): void {
    const entity = ent as Entity2D;
    const view = entity.c.view2d.view;
    const layer = this._layers[entity.c.view2d.layer];
    const index = layer.children.indexOf(view);

    if (index !== -1) {
      layer.removeChild(entity.c.view2d.view);
      //   view.parent = null;
      //   utils.removeItems(layer.children, index, 1);
    }
  }

  public reset(): void {
    for (const i in this._layers) {
      this._layers[i].removeChildren();
    }
  }

  public addLayer(id: string, index = -1): void {
    this._layers[id] = new Container();
    this._layers[id].tag = `View2DLayer:${id}`;

    if (index === -1) {
      index = this._activeItemsContainer.children.length;
    }

    this._activeItemsContainer.addChildAt(this._layers[id], index);
  }

  /**
   * Changes an entities layer
   * @param entity - entity to swap layer
   * @param newLayer - id of layer to swap too
   * @param depth - depth to add the entity at
   */
  public setEntityLayer(entity: Entity2D, newLayer: string, depth = -1): void {
    if (!this._layers[newLayer]) {
      throw new Error(`Layer doesn't exist: ${newLayer}`);
    }

    const view2d = entity.getComponent(View2DComponent);

    if (view2d) {
      const view = view2d.view;
      const layer = this._layers[view2d.layer];
      const index = layer.children.indexOf(view);

      if (index !== -1) {
        layer.removeChild(view);
      }

      if (depth === -1) {
        depth = this._layers[newLayer].children.length;
      }

      this._layers[newLayer].addChildAt(view, depth);
      view2d.layer = newLayer;
    }
  }

  /**
   * Sets a layer to a specific index
   * @param layerName - name of the layer
   * @param index - index to add the layer at
   */
  public setLayerAt(layerName: string, index: number) {
    const layer = this.layers[layerName];

    if (!layer) return;

    this.activeItemsContainer.addChildAt(layer, index);
  }

  public resize(w: number, h: number): void {
    if (this.w === w && this.h === h) return;
    this.w = w;
    this.h = h;
  }

  public get layers(): Record<string, Container> {
    return this._layers;
  }

  public get activeItemsContainer(): Container {
    return this._activeItemsContainer;
  }

  public get innerContainer(): Container {
    return this.innerContainer;
  }
}
